'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { format, subDays } from 'date-fns';
import { Inbox, Loader2, Search, X } from 'lucide-react';
import AIScansShell from '@/components/ai-scans/AIScansShell';
import AnnouncementCard from '@/components/ai-scans/AnnouncementCard';
import ErrorState from '@/components/ai-scans/ErrorState';
import { ExchangeBadge } from '@/components/ai-scans/MetricValue';
import { formatCount, formatDayHeading, stockHref } from '@/components/ai-scans/format';
import { getScreenerAnnouncements } from '@/lib/api/screenerApi';
import type { ScreenerAnnouncement, ScreenerQueryParams } from '@/types/screener';

const PAGE_SIZE = 30;
const isoDate = (date: Date) => format(date, 'yyyy-MM-dd');

const DATE_PRESETS = [
  { label: 'Any time', days: null },
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 6 },
  { label: 'Last 30 days', days: 29 },
];

// Phrases that match how companies word these filings (the search matches inside the text).
const TOPICS = [
  { label: 'Results', query: 'financial result' },
  { label: 'Board meetings', query: 'board meeting' },
  { label: 'Dividends', query: 'dividend' },
  { label: 'Acquisitions', query: 'acquisition' },
  { label: 'Credit ratings', query: 'credit rating' },
];

const DATE_INPUT =
  'w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:[color-scheme:dark]';

function groupByDay(items: ScreenerAnnouncement[]) {
  const groups: { day: string; items: ScreenerAnnouncement[] }[] = [];
  items.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.day === item.announced_on) last.items.push(item);
    else groups.push({ day: item.announced_on, items: [item] });
  });
  return groups;
}

function AnnouncementsContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const queryString = searchParams.toString();
  const urlQuery = searchParams.get('q') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const exchange = searchParams.get('exchange') ?? '';
  const symbol = searchParams.get('symbol') ?? '';
  const stockFilter = exchange && symbol ? { exchange, symbol } : null;

  const [items, setItems] = useState<ScreenerAnnouncement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [loadMoreError, setLoadMoreError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const updateParams = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(queryString);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      });
      const nextQuery = next.toString();
      router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, { scroll: false });
    },
    [queryString, router, pathname],
  );

  const [searchText, setSearchText] = useState(urlQuery);
  const pushedQuery = useRef(urlQuery);

  useEffect(() => {
    if (urlQuery !== pushedQuery.current) {
      pushedQuery.current = urlQuery;
      setSearchText(urlQuery);
    }
  }, [urlQuery]);

  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed === urlQuery) return;
    const timer = setTimeout(() => {
      pushedQuery.current = trimmed;
      updateParams({ q: trimmed || null });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, urlQuery, updateParams]);

  const apiParams = useMemo(() => {
    const params: ScreenerQueryParams = { limit: PAGE_SIZE };
    new URLSearchParams(queryString).forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [queryString]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadMoreError(null);
    getScreenerAnnouncements({ ...apiParams, offset: 0 })
      .then((response) => {
        if (cancelled) return;
        setItems(response.items);
        setTotal(response.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiParams, attempt]);

  const loadMore = async () => {
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const response = await getScreenerAnnouncements({ ...apiParams, offset: items.length });
      setItems((previous) => [...previous, ...response.items]);
      setTotal(response.total);
    } catch (err) {
      setLoadMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const applyPreset = (days: number | null) => {
    if (days === null) updateParams({ from: null, to: null });
    else updateParams({ from: isoDate(subDays(new Date(), days)), to: isoDate(new Date()) });
  };

  const today = isoDate(new Date());
  const activePreset = DATE_PRESETS.find(({ days }) =>
    days === null ? !from && !to : from === isoDate(subDays(new Date(), days)) && to === today,
  );
  const stockName = stockFilter ? items[0]?.name : undefined;
  const groups = groupByDay(items);
  const hasFilters = Boolean(urlQuery || from || to || stockFilter);

  return (
    <>
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl dark:text-white">Announcements</h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500 md:text-base dark:text-zinc-400">
          What listed companies have told the stock exchange — results, board meetings, dividends, new orders and
          more — newest first.
        </p>
      </header>

      {stockFilter && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3">
          <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
            Showing announcements from
            <Link
              href={stockHref(stockFilter.exchange, stockFilter.symbol)}
              className="inline-flex items-center gap-1.5 font-semibold text-zinc-900 hover:text-emerald-700 dark:text-white dark:hover:text-emerald-400"
            >
              {stockName ?? stockFilter.symbol.toUpperCase()}
              <ExchangeBadge exchange={stockFilter.exchange} />
            </Link>
          </p>
          <button
            type="button"
            onClick={() => updateParams({ exchange: null, symbol: null })}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-zinc-600 hover:bg-white dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <X className="h-3.5 w-3.5" />
            All companies
          </button>
        </div>
      )}

      <section className="mt-5 rounded-2xl border border-zinc-200 bg-white p-3 sm:p-4 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search announcements, e.g. dividend, board meeting, order…"
            aria-label="Search announcements"
            className="w-full rounded-lg border border-zinc-300 bg-white py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          />
        </div>

        <div className="-mx-3 mt-3 flex items-center gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0 [scrollbar-width:none]">
          <span className="shrink-0 text-xs font-medium text-zinc-500 dark:text-zinc-400">Topics:</span>
          {TOPICS.map((topic) => {
            const active = urlQuery.toLowerCase() === topic.query;
            return (
              <button
                key={topic.query}
                type="button"
                onClick={() => updateParams({ q: active ? null : topic.query })}
                aria-pressed={active}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'border-zinc-200 text-zinc-600 hover:border-emerald-500/50 dark:border-zinc-700 dark:text-zinc-400'
                }`}
              >
                {topic.label}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:px-0 [scrollbar-width:none]">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyPreset(preset.days)}
                aria-pressed={activePreset === preset}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  activePreset === preset
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'border-zinc-200 text-zinc-600 hover:border-emerald-500/50 dark:border-zinc-700 dark:text-zinc-400'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <label className="flex flex-col gap-1 text-xs text-zinc-500 sm:flex-row sm:items-center sm:gap-2 dark:text-zinc-400">
              From
              <input
                type="date"
                value={from}
                max={to || today}
                onChange={(event) => updateParams({ from: event.target.value || null })}
                className={DATE_INPUT}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-500 sm:flex-row sm:items-center sm:gap-2 dark:text-zinc-400">
              To
              <input
                type="date"
                value={to}
                min={from || undefined}
                max={today}
                onChange={(event) => updateParams({ to: event.target.value || null })}
                className={DATE_INPUT}
              />
            </label>
          </div>
        </div>
      </section>

      <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400" aria-live="polite">
        {!loading && !error && total > 0 && (
          <>
            <strong className="font-semibold text-zinc-800 dark:text-zinc-200">{formatCount(total)}</strong>{' '}
            announcement{total === 1 ? '' : 's'}
          </>
        )}
      </p>

      <div className="mt-2">
        {error ? (
          <ErrorState error={error} onRetry={() => setAttempt((n) => n + 1)} />
        ) : loading ? (
          <div className="space-y-3" aria-busy>
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                <div className="h-3 w-40 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="mt-2 h-3 w-full animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/60" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
            <Inbox className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
            <p className="mt-4 text-base font-semibold text-zinc-800 dark:text-zinc-200">No announcements found</p>
            <p className="mt-1 max-w-sm text-sm text-zinc-500 dark:text-zinc-400">
              {hasFilters ? 'Try a different word or a wider date range.' : 'New filings will show up here as companies publish them.'}
            </p>
            {hasFilters && (
              <button
                type="button"
                onClick={() => router.replace(pathname, { scroll: false })}
                className="mt-4 text-sm font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map((group) => (
              <section key={group.day}>
                <h2 className="sticky top-16 z-10 -mx-1 mb-3 bg-zinc-50/90 px-1 py-2 text-sm font-semibold text-zinc-900 backdrop-blur dark:bg-zinc-950/90 dark:text-white">
                  {formatDayHeading(group.day)}
                </h2>
                <div className="space-y-3">
                  {group.items.map((announcement) => (
                    <AnnouncementCard key={announcement.id} announcement={announcement} showCompany={!stockFilter} />
                  ))}
                </div>
              </section>
            ))}

            {loadMoreError ? <ErrorState error={loadMoreError} onRetry={loadMore} compact /> : null}

            {items.length < total && (
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:border-emerald-500/50 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                >
                  {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
                <span className="text-xs text-zinc-500">
                  Showing {formatCount(items.length)} of {formatCount(total)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function AnnouncementsPage() {
  return (
    <AIScansShell>
      <Suspense fallback={<div className="h-96 animate-pulse rounded-2xl bg-zinc-100 dark:bg-zinc-900" />}>
        <AnnouncementsContent />
      </Suspense>
    </AIScansShell>
  );
}
